// Carriers to tick, grouped by coverage line.
//
// Used in three places that must look identical: quote mode's step 4, the RFP's
// direct mode, and each GA's pane in the RFP's GA mode. The only thing that
// differs between them is WHICH carriers are offered, so that is a prop.
//
// Line colours and order come from getPlanTypeAccent / planTypeRank rather than
// a palette of their own, which is what keeps this in step with the plan cards
// elsewhere in the app and gives dark mode for free.

import type { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { normalizePlanType } from "@shared/schema";
import { getPlanTypeAccent, planTypeRank } from "@shared/schemas/planTypes";
import { getCoverageLabel } from "../constants";
import type { Carrier } from "@shared/schema";

export function CarrierListByLine({
  coverageTypes,
  getCarriers,
  isTicked,
  onToggle,
  disabledReason,
  renderHint,
  emptyMessage,
  loading,
}: {
  coverageTypes: string[];
  /** Already filtered: active, supports the line, quotable in the client's state. */
  getCarriers: (coverageType: string) => Carrier[];
  isTicked: (carrierId: number) => boolean;
  onToggle: (carrier: Carrier, coverageType: string) => void;
  /**
   * Why this carrier cannot be ticked here, or null when it can. Greyed rather
   * than dropped: a carrier missing from the list is indistinguishable from one
   * that does not exist.
   */
  disabledReason?: (carrier: Carrier, coverageType: string) => string | null;
  /** Right-hand note on a row, e.g. which other GA is also asked for it. */
  renderHint?: (carrierId: number) => ReactNode;
  emptyMessage?: (coverageType: string) => string;
  loading?: boolean;
}) {
  // Canonical order, so Medical always precedes Dental wherever this renders.
  const lines = [...coverageTypes].sort((a, b) => planTypeRank(a) - planTypeRank(b));

  if (loading) {
    return <p className="text-sm text-muted-foreground">Checking which carriers are available…</p>;
  }

  return (
    <div className="space-y-4">
      {lines.map((coverageType) => {
        const carriers = getCarriers(coverageType);
        // Counted against what can be ticked, or "2 of 9" reads as seven
        // carriers the broker forgot.
        const selectable = carriers.filter(
          (carrier) => !disabledReason?.(carrier, coverageType),
        );
        const tickedCount = selectable.filter((carrier) => isTicked(carrier.id)).length;
        const accent = getPlanTypeAccent(normalizePlanType(coverageType));

        return (
          <div key={coverageType} className="space-y-2">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  accent.accentBg,
                  accent.accentText,
                )}
              >
                {getCoverageLabel(coverageType)}
              </span>
              {selectable.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {tickedCount} of {selectable.length}
                </span>
              )}
            </div>

            {carriers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {emptyMessage?.(coverageType) ?? "No carriers available for this coverage."}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {carriers.map((carrier) => {
                  const reason = disabledReason?.(carrier, coverageType) ?? null;
                  const ticked = !reason && isTicked(carrier.id);
                  return (
                    <button
                      key={carrier.id}
                      type="button"
                      aria-pressed={ticked}
                      disabled={!!reason}
                      title={reason ?? carrier.name}
                      onClick={() => onToggle(carrier, coverageType)}
                      className={cn(
                        "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors",
                        reason
                          ? "cursor-not-allowed border-dashed opacity-50"
                          : ticked
                            ? "border-primary/40 bg-primary/5"
                            : "hover:bg-muted/40",
                      )}
                    >
                      {/* The row is the click target, so the box must not take
                          the click as well or every tick fires twice. */}
                      <Checkbox
                        checked={ticked}
                        disabled={!!reason}
                        className="pointer-events-none shrink-0"
                      />
                      <span
                        className={cn("min-w-0 flex-1 truncate text-sm", ticked && "font-medium")}
                      >
                        {carrier.name}
                      </span>
                      {reason ? (
                        <span className="shrink-0 text-[11px] text-muted-foreground">{reason}</span>
                      ) : (
                        renderHint?.(carrier.id)
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
